// src/publish/normalize.js — backend text (HTML / Markdown / "[N]" markers) → blocks.
//
// The Writer's fields are not plain text: blog, newsletter, email and landing
// may carry inline HTML (<strong>, <em>, <a href>, <ul><li>, <br>), social posts
// carry Markdown bold (**…**) and emoji, the blog carries "[N]" citation
// markers (see the proposal's «La marcatura dentro i campi»). This module reads
// that mix ONCE into the block model of blocks.js, so every output downstream
// is a conversion and never sees raw markup.
//
// Rules (the proposal's «Regole di normalizzazione», pinned by the tests):
//   <strong>, <b>, **…**        → strong run (**…** only with non-space boundaries,
//                                 as CommonMark reads it: "** b **" stays text)
//   <em>, <i>, <cite>           → em run
//   <a href="…">…</a>           → link run ONLY for an absolute http(s) href
//                                 (isHttpHref): an <a> without href, or with a
//                                 javascript:, data:, vbscript:, file: or relative
//                                 one, is dropped and its text kept — a link run
//                                 is written verbatim by the html/docx serialisers
//   <ul>/<ol>/<li>              → list block (a list inside an item is flattened to
//                                 line breaks inside that item; a list never closed
//                                 ends with the input)
//   <p>, blank lines            → paragraph boundary (other block-level tags too:
//                                 <div>, <h1..6>, <blockquote>, <tr>…, their text kept)
//   <td>, <th>                  → a space between cells ("Piano" | "29 €" never glue)
//   <dt>, <dd>, <li> outside a list → a line break (br run)
//   <br>, a single newline      → br run inside the paragraph
//   "[N]"                       → cite run (n integer)
//   <script>, <style>           → dropped WITH their content
//   any other HTML tag          → dropped, its text kept (a quoted attribute may
//                                 hold ">" — the tag still ends at the real close)
//   "<…>" that is NOT an HTML   → literal text: "<nome azienda>" and "x<y e z>w"
//   element name (HTML_TAGS)      survive, so a placeholder or a comparison is
//                                 never swallowed up to the next ">"
//   HTML entities               → decoded (&amp; &#39; &#x27; …)
//   whitespace                  → trimmed per line and per block
//   "## " / "### " lines        → heading blocks ONLY with opts.headings (fields
//                                 that carry their own Markdown headings); a "# "
//                                 line gives a level-1 heading the same way, for a
//                                 string content that opens with its own title
//
// Never throws: null, undefined or a non-string give [].

import {
  text, strong, em, link, cite, br, heading, paragraph, list, compactBlocks,
} from './blocks.js';

// One regex instance per loop: a shared global would carry `lastIndex` from a
// nested call (normalizeText → inlineRuns) back into the outer loop.
// Attributes: a quoted value may contain ">", so a tag ends at the first ">"
// outside quotes (the pattern is shared by every tag-matching regex below).
const ATTRS = '(?:"[^"]*"|\'[^\']*\'|[^>"\'])*';
const tagRe = () => new RegExp(`<(\\/?)([a-zA-Z][a-zA-Z0-9:-]*)\\b(${ATTRS})>`, 'g');
const listTagRe = () => new RegExp(`<(\\/?)(ul|ol)\\b${ATTRS}>`, 'gi');
const listItemTagRe = () => new RegExp(`<(\\/?)(ul|ol|li)\\b${ATTRS}>`, 'gi');
const SCRIPT_STYLE_RE = /<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
const citeRe = () => /\[(\d+)\]/g;
// CommonMark's bold: the text inside opens and closes on a non-space character.
// No lookbehind anywhere in this file: Babel does not transpile it and Safari /
// iOS ≤ 16.3 cannot parse the bundle (the test reads this file and pins it).
const MD_BOLD_RE = /\*\*(?!\s)([^*]*?[^*\s])\*\*/g;
const MD_HEADING_RE = /^(#{1,3})\s+(.+?)\s*$/;
// Cell tags → one space; line tags (and <li> outside a list) → one line break.
// A run of adjacent tags collapses into one separator; at the very start or end
// of the fragment the separator is dropped (nothing to separate).
const CELL_TAGS_RE = new RegExp(`(?:[ \\t]*<\\/?(?:td|th)\\b${ATTRS}>[ \\t]*)+`, 'gi');
const LINE_TAGS_RE = new RegExp(`(?:\\s*<\\/?(?:dt|dd|li)\\b${ATTRS}>\\s*)+`, 'gi');
const separator = (sep) => (m, offset, whole) => (offset === 0 || offset + m.length === whole.length ? '' : sep);

const INLINE_TAGS = new Set(['strong', 'b', 'em', 'i', 'cite', 'a']);
const BLOCK_TAGS = new Set([
  'p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'section', 'article',
  'header', 'footer', 'table', 'tr', 'pre', 'hr', 'figure', 'figcaption',
]);
const LIST_TAGS = new Set(['ul', 'ol', 'li']);
const CELL_TAGS = new Set(['td', 'th', 'dt', 'dd']);
// The other HTML elements a backend field may carry: dropped, their text kept.
// A "<name …>" whose name is in none of these sets is text, not markup.
const OTHER_HTML_TAGS = new Set([
  'br', 'script', 'style', 'span', 'u', 's', 'mark', 'small', 'sub', 'sup', 'code', 'kbd', 'samp',
  'var', 'abbr', 'q', 'dfn', 'time', 'del', 'ins', 'wbr', 'img', 'picture', 'source', 'video',
  'audio', 'iframe', 'object', 'embed', 'svg', 'path', 'label', 'input', 'button', 'select', 'option',
  'textarea', 'form', 'fieldset', 'legend', 'details', 'summary', 'dialog', 'nav', 'main', 'aside',
  'address', 'thead', 'tbody', 'tfoot', 'caption', 'colgroup', 'col', 'dl', 'html', 'head', 'body',
  'title', 'meta', 'link', 'base', 'noscript', 'template', 'font', 'center', 'strike', 'tt', 'big',
  'bdi', 'bdo', 'ruby', 'rt', 'rp', 'data', 'meter', 'progress', 'output', 'canvas', 'map', 'area',
  'track', 'param', 'menu', 'hgroup', 'search', 'slot',
]);
const HTML_TAGS = new Set([...INLINE_TAGS, ...BLOCK_TAGS, ...LIST_TAGS, ...CELL_TAGS, ...OTHER_HTML_TAGS]);

/** Only an absolute http(s) address is a link anywhere (link runs, the screen's <a>). */
export const isHttpHref = (href) => typeof href === 'string' && /^https?:\/\//i.test(href.trim());

// ─── Entities ────────────────────────────────────────────────────────────────

const NAMED_ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  copy: '©', reg: '®', trade: '™', hellip: '…', mdash: '—', ndash: '–',
  laquo: '«', raquo: '»', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
  euro: '€', pound: '£', deg: '°', middot: '·', bull: '•', rarr: '→', larr: '←',
  agrave: 'à', egrave: 'è', eacute: 'é', igrave: 'ì', ograve: 'ò', ugrave: 'ù',
  Agrave: 'À', Egrave: 'È', Eacute: 'É', Igrave: 'Ì', Ograve: 'Ò', Ugrave: 'Ù',
  aacute: 'á', iacute: 'í', oacute: 'ó', uacute: 'ú', ccedil: 'ç', ntilde: 'ñ',
  uuml: 'ü', ouml: 'ö', auml: 'ä', szlig: 'ß', ecirc: 'ê', acirc: 'â', ocirc: 'ô',
};

/** Decodes named, decimal and hex entities; unknown ones are left as written. */
export function decodeEntities(s) {
  if (typeof s !== 'string' || s.indexOf('&') === -1) return s ?? '';
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]*);/g, (m, body) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      if (!Number.isFinite(code) || code <= 0 || code > 0x10FFFF) return m;
      try { return String.fromCodePoint(code); } catch (_e) { return m; }
    }
    return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, body) ? NAMED_ENTITIES[body] : m;
  });
}

// ─── Inline ──────────────────────────────────────────────────────────────────

/**
 * One text fragment (no block structure expected) → runs.
 * Markdown bold, inline HTML, <br>, "[N]" and entities are interpreted; any
 * other HTML tag is dropped and its text kept; a "<…>" that names no HTML
 * element is literal text. "\n" becomes a br run.
 */
export function inlineRuns(fragment) {
  if (typeof fragment !== 'string' || !fragment) return [];
  let s = fragment.replace(/\r\n?/g, '\n').replace(SCRIPT_STYLE_RE, '');
  s = s.replace(MD_BOLD_RE, '<strong>$1</strong>');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(CELL_TAGS_RE, separator(' ')).replace(LINE_TAGS_RE, separator('\n'));

  const root = { runs: [] };
  const stack = [root];             // open inline containers, root first
  const current = () => stack[stack.length - 1];

  const emitText = (chunk) => {
    if (!chunk) return;
    const decoded = decodeEntities(chunk);
    let last = 0;
    const re = citeRe();
    let m;
    while ((m = re.exec(decoded)) !== null) {
      emitPlain(decoded.slice(last, m.index));
      current().runs.push(cite(parseInt(m[1], 10)));
      last = m.index + m[0].length;
    }
    emitPlain(decoded.slice(last));
  };
  const emitPlain = (chunk) => {
    if (!chunk) return;
    const lines = chunk.split('\n');
    lines.forEach((line, i) => {
      if (i > 0) current().runs.push(br());
      if (line) current().runs.push(text(line));
    });
  };

  let pos = 0;
  const re = tagRe();
  let m;
  while ((m = re.exec(s)) !== null) {
    const tag = m[2].toLowerCase();
    if (!HTML_TAGS.has(tag)) continue;          // not an element name: literal text
    emitText(s.slice(pos, m.index));
    pos = m.index + m[0].length;
    const closing = m[1] === '/';
    const attrs = m[3] || '';
    if (!INLINE_TAGS.has(tag)) continue;        // other HTML tag: dropped, text kept
    if (closing) {
      const idx = findOpen(stack, tag);
      if (idx > 0) stack.length = idx;           // close it and anything left open inside
      continue;
    }
    if (attrs.trim().endsWith('/')) continue;    // self-closing inline tag: nothing to wrap
    let node;
    if (tag === 'strong' || tag === 'b') node = strong([]);
    else if (tag === 'em' || tag === 'i' || tag === 'cite') node = em([]);
    else {
      const href = hrefOf(attrs);
      if (!href) { stack.push({ runs: current().runs, transparent: true, tag: 'a' }); continue; }
      node = link(href, []);
    }
    node.tag = tag;
    current().runs.push(node);
    stack.push(node);
  }
  emitText(s.slice(pos));
  return finishRuns(root.runs);
}

function findOpen(stack, tag) {
  const aliases = tag === 'b' ? ['strong', 'b'] : tag === 'strong' ? ['strong', 'b']
    : tag === 'a' ? ['a'] : ['em', 'i', 'cite'];
  for (let i = stack.length - 1; i > 0; i -= 1) {
    if (aliases.includes(stack[i].tag)) return i;
  }
  return -1;
}

function hrefOf(attrs) {
  const m = /href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs);
  const raw = m ? (m[1] ?? m[2] ?? m[3] ?? '') : '';
  const href = decodeEntities(raw).trim();
  return isHttpHref(href) ? href : '';
}

/** Strips the parser's bookkeeping, merges adjacent text runs, drops empty wrappers. */
function finishRuns(runs) {
  const out = [];
  for (const r of runs) {
    if (r.type === 'strong' || r.type === 'em' || r.type === 'link') {
      const inner = finishRuns(r.runs);
      if (inner.length === 0) continue;
      const node = r.type === 'link' ? link(r.href, inner) : r.type === 'strong' ? strong(inner) : em(inner);
      out.push(node);
      continue;
    }
    if (r.type === 'text') {
      const prev = out[out.length - 1];
      if (prev && prev.type === 'text') { prev.text += r.text; continue; }
      if (!r.text) continue;
      out.push(text(r.text));
      continue;
    }
    out.push(r);
  }
  return out;
}

// ─── Blocks ──────────────────────────────────────────────────────────────────

/**
 * A backend text field → blocks (paragraphs, lists and, with opts.headings,
 * headings). Never throws; non-strings give [].
 */
export function normalizeText(input, opts = {}) {
  if (typeof input !== 'string' || !input.trim()) return [];
  const headings = Boolean(opts.headings);
  // Markdown bold is read per paragraph by inlineRuns — one pass, never two.
  let s = input.replace(/\r\n?/g, '\n').replace(SCRIPT_STYLE_RE, '');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(tagRe(), (m, closing, tag) => (BLOCK_TAGS.has(tag.toLowerCase()) ? '\n\n' : m));

  const out = [];
  let last = 0;
  const re = tagRe();
  let m;
  while ((m = re.exec(s)) !== null) {
    const tag = m[2].toLowerCase();
    if (m[1] === '/' || (tag !== 'ul' && tag !== 'ol')) continue;
    const open = m.index + m[0].length;
    // A list never closed ends with the input: its items are still items.
    const end = matchingClose(s, tag, open) || { start: s.length, end: s.length };
    pushParagraphs(out, s.slice(last, m.index), headings);
    out.push(listFromHtml(tag === 'ol', s.slice(open, end.start)));
    last = end.end;
    re.lastIndex = last;
  }
  pushParagraphs(out, s.slice(last), headings);
  return compactBlocks(out);
}

/** Finds the close of the list opened at `from`, honouring nested lists; null when none. */
function matchingClose(s, tag, from) {
  const re = listTagRe();
  re.lastIndex = from;
  let depth = 1;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m[1] === '/') {
      depth -= 1;
      if (depth === 0) return { start: m.index, end: m.index + m[0].length };
    } else {
      depth += 1;
    }
  }
  return null;
}

/**
 * The inside of a <ul>/<ol> → a list block. Only the <li> at the list's own
 * level start items; a nested list flattens into its item as line breaks.
 * Text before the first <li> is dropped.
 */
function listFromHtml(ordered, inner) {
  const starts = [];                      // [start, end] of each top-level <li> tag
  const re = listItemTagRe();
  let depth = 0;
  let m;
  while ((m = re.exec(inner)) !== null) {
    const tag = m[2].toLowerCase();
    if (tag === 'li') { if (depth === 0 && m[1] !== '/') starts.push([m.index, m.index + m[0].length]); continue; }
    depth += m[1] === '/' ? -1 : 1;
  }
  const items = [];
  starts.forEach(([, from], i) => {
    const to = i + 1 < starts.length ? starts[i + 1][0] : inner.length;
    const body = inner.slice(from, to)
      .replace(listItemTagRe(), '\n')
      .split('\n').map((l) => l.trim()).filter(Boolean).join('\n');
    const runs = inlineRuns(body);
    if (runs.length) items.push(runs);
  });
  return list(items, ordered);
}

/** Text between lists → paragraphs (and headings when enabled). */
function pushParagraphs(out, chunk, headings) {
  if (!chunk || !chunk.trim()) return;
  for (const para of chunk.split(/\n\s*\n/)) {
    const lines = para.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!lines.length) continue;
    if (!headings) { out.push(paragraph(inlineRuns(lines.join('\n')))); continue; }
    let pending = [];
    const flush = () => {
      if (pending.length) out.push(paragraph(inlineRuns(pending.join('\n'))));
      pending = [];
    };
    for (const line of lines) {
      const h = MD_HEADING_RE.exec(line);
      if (h) { flush(); out.push(heading(h[1].length, inlineRuns(h[2]))); }
      else pending.push(line);
    }
    flush();
  }
}
